import { Navigate, useLocation } from 'react-router-dom';
import { Suspense, lazy, type ReactNode } from 'react';
import { AccountingPanels } from './AccountingPanels';
import { AccountingSkeleton } from './ui';
import { viewFromPathname, type AccountingViewId } from './tabRoutes';

const JournalBookPage = lazy(() =>
  import('./books/JournalBookPage').then((m) => ({ default: m.JournalBookPage })),
);
const LedgerBookPage = lazy(() =>
  import('./books/LedgerBookPage').then((m) => ({ default: m.LedgerBookPage })),
);
const TrialBookPage = lazy(() =>
  import('./books/TrialBookPage').then((m) => ({ default: m.TrialBookPage })),
);
const MonthCloseWizardPage = lazy(() =>
  import('./close/MonthCloseWizardPage').then((m) => ({ default: m.MonthCloseWizardPage })),
);
const ClosePeriodsHistoryPage = lazy(() =>
  import('./close/ClosePeriodsHistoryPage').then((m) => ({ default: m.ClosePeriodsHistoryPage })),
);
const IncomeStatementPage = lazy(() =>
  import('./statements/IncomeStatementPage').then((m) => ({ default: m.IncomeStatementPage })),
);
const BalanceSheetPage = lazy(() =>
  import('./statements/BalanceSheetPage').then((m) => ({ default: m.BalanceSheetPage })),
);
const CashFlowPage = lazy(() =>
  import('./statements/CashFlowPage').then((m) => ({ default: m.CashFlowPage })),
);
const ImportsWizardPage = lazy(() =>
  import('./tools/ImportsWizardPage').then((m) => ({ default: m.ImportsWizardPage })),
);
const InflationWizardPage = lazy(() =>
  import('./tools/InflationWizardPage').then((m) => ({ default: m.InflationWizardPage })),
);
const FxToolsPage = lazy(() =>
  import('./tools/FxToolsPage').then((m) => ({ default: m.FxToolsPage })),
);
const ChartOfAccountsPage = lazy(() =>
  import('./masters/ChartOfAccountsPage').then((m) => ({ default: m.ChartOfAccountsPage })),
);
const CostCentersPage = lazy(() =>
  import('./masters/DimensionMasterPage').then((m) => ({ default: m.CostCentersPage })),
);
const BusinessUnitsPage = lazy(() =>
  import('./masters/DimensionMasterPage').then((m) => ({ default: m.BusinessUnitsPage })),
);
const MappingsPage = lazy(() =>
  import('./masters/MappingsPage').then((m) => ({ default: m.MappingsPage })),
);

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<AccountingSkeleton kind="report" />}>{children}</Suspense>;
}

/** Renders route-mapped accounting views. */
export function AccountingViewPage({ view: viewProp }: { view?: AccountingViewId }) {
  const { pathname } = useLocation();
  const view = viewProp ?? viewFromPathname(pathname);
  if (!view) return <Navigate to="/accounting" replace />;
  if (view === 'dashboard') return <Navigate to="/accounting" replace />;

  const page =
    view === 'journal' ? (
      <JournalBookPage />
    ) : view === 'ledger' ? (
      <LedgerBookPage />
    ) : view === 'trial' ? (
      <TrialBookPage />
    ) : view === 'closeWizard' ? (
      <MonthCloseWizardPage />
    ) : view === 'periods' ? (
      <ClosePeriodsHistoryPage />
    ) : view === 'income' ? (
      <IncomeStatementPage />
    ) : view === 'balance' ? (
      <BalanceSheetPage />
    ) : view === 'cashflow' ? (
      <CashFlowPage />
    ) : view === 'imports' ? (
      <ImportsWizardPage />
    ) : view === 'inflation' ? (
      <InflationWizardPage />
    ) : view === 'multicurrency' ? (
      <FxToolsPage />
    ) : view === 'chart' ? (
      <ChartOfAccountsPage />
    ) : view === 'costCenters' ? (
      <CostCentersPage />
    ) : view === 'businessUnits' ? (
      <BusinessUnitsPage />
    ) : view === 'mappings' ? (
      <MappingsPage />
    ) : (
      <AccountingPanels view={view === 'fiscalYears' ? 'periods' : view} />
    );

  if (
    view === 'journal' ||
    view === 'ledger' ||
    view === 'trial' ||
    view === 'closeWizard' ||
    view === 'periods' ||
    view === 'income' ||
    view === 'balance' ||
    view === 'cashflow' ||
    view === 'imports' ||
    view === 'inflation' ||
    view === 'multicurrency' ||
    view === 'chart' ||
    view === 'costCenters' ||
    view === 'businessUnits' ||
    view === 'mappings'
  ) {
    return <Lazy>{page}</Lazy>;
  }
  return page;
}
